import classNames from 'classnames'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useNavigate, useParams} from 'react-router-dom'
import {ImgButton} from '../../../components/core/button/index.jsx'
import Confirm from '../../../components/core/confirm/index.jsx'
import {PageTitle, ResponsivePageTitle} from '../../../components/core/title/index.jsx'
import Footer from '../../../components/layouts/footer/index.jsx'
import Modals from '../../../components/pages/serverMonitoring/Modals.jsx'
import modalTypes from '../../../components/pages/serverMonitoring/modalTypes.json'
import os from '../../../data/selectOptions/os.json'
import {PlusIcon, SecureJobsIcon} from '../../../icons/index.jsx'
import {getAllLimitUsages} from '../../../stores/auth/index.js'
import {getServerResources} from '../../../stores/resources/index.js'
import {getServerResource, removeRuleset} from '../../../stores/server-monitoring/index.js'
import Customer from '../../../utils/customer.js'
import LimitManager from '../../../utils/limitManager.js'
import EmptyContent from '../../resources/EmptyContent.jsx'
import '../index.scss'
import PageBody from './PageBody.jsx'
import PageHeader from './PageHeader.jsx'
import useHeaderActions from '../UseHeaderActions.jsx'
import {useNamespaceTranslations} from '../../../utils/hooks/useNamespacesTranslations.js'
import {envConfig} from '../../../utils/env.js'

const getServerGroupedMetricValues = serverGroupedMetricTypes => Object.values(serverGroupedMetricTypes)?.flatMap(item => item)
const {publicApiBaseUrl, isReadOnlyModeEnabled} = envConfig

const ServerResourceDashboard = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const {id} = useParams()
    const text = useNamespaceTranslations('server-monitoring')
    const rulesetsText = useNamespaceTranslations('rulesets')
    const serverMetrics = useNamespaceTranslations('server-metrics')?.server_metrics
    const usageLimitsData = useNamespaceTranslations('usage-limits')

    const serverMetricValues = useMemo(() => Object.values(serverMetrics), [serverMetrics])
    const osInstructionCodes = text.steps.instructions.items.os_types

    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const {
        isMetricsDataRetrieving,
        isResourceFetching,
        serverResource,
        isButtonLoading,
        metricSources,
        rulesets
    } = useSelector((state) => state.server_monitoring)
    const commonMetricsInfo = useSelector(state => state.server_monitoring?.[serverMetrics.cpu_total_usage.value]?.commonMetricsInfo)
    const {
        isDeleting,
        serverResources
    } = useSelector(state => state.resources)

    const serverGroupedMetricTypes = serverResource?.grouped_metric_types || {}
    const selectedOsOption = useMemo(() => Object.values(os).find((opt) => opt.value === serverResource?.os), [serverResource])
    const [isFirstLoad, setIsFirstLoad] = useState(true)
    const [showUpdateWarning, setShowUpdateWarning] = useState(false)
    const [activeMetrics, setActiveMetrics] = useState([])

    const [filteredMetrics, setFilteredMetrics] = useState(serverMetricValues
        ?.filter(serverMetric => getServerGroupedMetricValues(serverGroupedMetricTypes)?.includes(serverMetric?.value)))
    const [instructionModalContent, setInstructionModalContent] = useState({})
    const [showBackButton, setShowBackButton] = useState(false)
    const [rulesetPendingDelete, setRulesetPendingDelete] = useState(null)

    const dashboardPath = `/server-monitoring/${id}`
    const isServerMonitoringDisabled = !LimitManager.getServerResourceLimit(usageLimitsData)
    const {
        deleteResource,
        handleAddServer,
        selectedMetricTypes,
        usageLimits,
        rulesetFormInitialValues,
        activeModal,
        setActiveModal,
        fetchRulesets
    } = useHeaderActions({isFetching: isResourceFetching, showStatusBtns: false, serverResource})
    const selectedMetricRulesets = rulesets

    const toggleInstructionModal = useCallback(() => {
        setShowUpdateWarning(false)
        setActiveModal({...activeModal, type: modalTypes.instruction})
    }, [activeModal])

    const confirmDeleteRuleset = useCallback(() => {
        dispatch(removeRuleset(rulesets.indexOf(rulesetPendingDelete)))
        setRulesetPendingDelete(null)
    }, [dispatch, rulesets, rulesetPendingDelete])

    useEffect(() => {
        const updatedMetrics = serverMetricValues
            ?.filter(serverMetric => getServerGroupedMetricValues(serverGroupedMetricTypes)?.includes(serverMetric?.value))
        setActiveMetrics(updatedMetrics)
        setFilteredMetrics(updatedMetrics)
    }, [serverGroupedMetricTypes?.basic,
        serverGroupedMetricTypes?.advanced,
        serverGroupedMetricTypes?.complex])

    useEffect(() => {
        setIsFirstLoad(false)
        if (!serverResources?.length) dispatch(getServerResources(true))
        dispatch(getServerResource(serverMetrics, true, id, navigate, false, false))
        dispatch(getAllLimitUsages())
    }, [id])

    const metricProps = useMemo(() => ({
        serverGroupedMetricValues: getServerGroupedMetricValues(serverGroupedMetricTypes),
        activeMetrics,
        metricSources,
        setActiveMetrics,
        filteredMetrics,
        setFilteredMetrics,
        commonMetricsInfo,
        serverMetricValues,
        isMetricsDataRetrieving
    }), [serverGroupedMetricTypes,
        activeMetrics,
        filteredMetrics,
        commonMetricsInfo,
        serverMetricValues,
        isMetricsDataRetrieving
    ])

    const serverResourceProps = useMemo(() => ({
        serverResource,
        serverResources,
        selectedMetricTypes,
        selectedOsOption,
        id,
        isFirstLoad,
        isResourceFetching
    }), [serverResource,
        serverResources,
        id,
        isFirstLoad,
        isResourceFetching
    ])

    const modalsProps = useMemo(() => ({
        instructionModalContent,
        setInstructionModalContent,
        osInstructionCodes,
        activeModal,
        setActiveModal,
        toggleInstructionModal,
        showUpdateWarning,
        setShowUpdateWarning,
        showBackButton,
        setShowBackButton,
        fetchRulesets
    }), [instructionModalContent,
        osInstructionCodes,
        activeModal,
        toggleInstructionModal,
        showUpdateWarning,
        showBackButton,
        setShowBackButton,
        fetchRulesets
    ])
    const telemetryUrl = `${publicApiBaseUrl}server/${id}/metrics`

    return (
        <main className={classNames('main monitoring', {
            dark: isDarkMode
        })} id="server-monitoring">
            {activeModal?.type && <Modals
                isVisible={activeModal?.type}
                activeModal={activeModal}
                setActiveModal={setActiveModal}
                showBackButton={showBackButton}
                setShowBackButton={setShowBackButton}
                uiConfig={{
                    isDarkMode,
                    telemetryUrl,
                    selectedOsOption,
                    selectedMetricTypes,
                    serverResource,
                    isMetricsDataRetrieving,
                    isResourceFetching
                }}
                rulesetConfig={{
                    rulesetData: selectedMetricRulesets,
                    rulesetFormInitialValues,
                    usageLimits,
                    rulesetTypeOptions: serverMetrics,
                    isCronJobRuleset: false,
                    onDeleteRuleset: setRulesetPendingDelete,
                    onOpenRulesetList: fetchRulesets
                }}
                serverMetricsConfig={{
                    selectedMetric: serverMetricValues?.find(metric => metric?.value === activeModal?.metric_type),
                    instructionModalContent
                }}
            />
            }
            <Confirm
                body={text.are_you_sure}
                callback={() => deleteResource([serverResource?.id])} showLoader={isDeleting} />
            <Confirm
                body={rulesetsText.are_you_sure}
                callback={confirmDeleteRuleset}
                isOpen={!!rulesetPendingDelete}
                onClose={() => setRulesetPendingDelete(null)}
            />
            <div>
                <ResponsivePageTitle />
                <div className="content-title">
                    <div className="left-side">
                        <div>
                            <span className={classNames('section', {
                                dark: isDarkMode
                            })}>{text.section}</span>
                            <PageTitle>
                                <h1 className={classNames('page-title', {
                                    dark: isDarkMode
                                })}>{text.title}</h1>
                            </PageTitle>
                        </div>
                    </div>
                    {Customer.isOwnTeam() && (
                        <ImgButton
                            onClick={() => handleAddServer(navigate, dashboardPath)} disabled={isReadOnlyModeEnabled}>
                            {text.add_server_monitoring}
                            <span><PlusIcon /></span>
                        </ImgButton>
                    )}
                </div>
                {isServerMonitoringDisabled && !isResourceFetching ?
                    <EmptyContent message={text.disabled_server_message} hideButton={true} icon={<SecureJobsIcon />} /> :
                    <>
                        <PageHeader
                            isButtonLoading={isButtonLoading}
                            serverResourceProps={serverResourceProps}
                            metricProps={metricProps}
                            modalsProps={modalsProps}
                            activeModal={activeModal}
                        />
                        <PageBody
                            serverResourceProps={serverResourceProps}
                            metricProps={metricProps}
                            modalsProps={modalsProps}
                            activeModal={activeModal}
                        />
                    </>}
            </div>
            <Footer />
        </main>
    )
}

export default ServerResourceDashboard
